// Единый источник правды по статусам тренировок.
// Используется в PlanPage, DashboardPage, WorkoutActionModal.

export type WorkoutStatus =
  | "planned"
  | "completed"
  | "done" // alias для completed (бэк иногда возвращает)
  | "cancelled"
  | "rescheduled";

export type StatusVisuals = {
  label: string;
  // микро-точка / индикатор
  dot: string;
  // бейдж текстом (фон + рамка + текст)
  badge: string;
  // левый акцент-бордер карточки (l-border)
  dim: boolean;
};

const VISUALS: Record<string, StatusVisuals> = {
  planned: {
    label: "План",
    dot: "bg-fg-muted",
    badge: "bg-bg-raised text-fg-secondary border border-bg-border",
    dim: false,
  },
  completed: {
    label: "Выполнено",
    dot: "bg-emerald-400",
    badge: "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30",
    dim: true,
  },
  done: {
    label: "Выполнено",
    dot: "bg-emerald-400",
    badge: "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30",
    dim: true,
  },
  cancelled: {
    label: "Отменено",
    dot: "bg-rose-400",
    badge: "bg-rose-500/15 text-rose-300 border border-rose-500/30",
    dim: true,
  },
  rescheduled: {
    label: "Перенесено",
    dot: "bg-amber-400",
    badge: "bg-amber-500/15 text-amber-300 border border-amber-500/30",
    dim: false,
  },
};

const FALLBACK: StatusVisuals = {
  label: "—",
  dot: "bg-fg-muted",
  badge: "bg-bg-raised text-fg-muted border border-bg-border",
  dim: false,
};

export function getStatusVisuals(status: string): StatusVisuals {
  return VISUALS[status] || FALLBACK;
}

/** true для обоих алиасов выполненной тренировки ("completed" | "done"). */
export function isDoneStatus(status: string | null | undefined): boolean {
  return status === "completed" || status === "done";
}

/**
 * true, если тренировки НЕ БУДЕТ.
 *
 * Деление «выполнена / всё остальное» ставит отменённую сессию в один ряд с
 * запланированной, и протокол питания продолжал советовать «выпей 15 г за 30
 * минут до старта» для тренировки, которой не будет.
 *
 * ⚠️ Только `cancelled`. `rescheduled` — это ПЕРЕНОС, а не отмена: бэкенд
 * меняет `scheduled_date` у той же строки и оставляет её предстоящей
 * (`api/routers/athlete_actions.py:689`, выборка ближайших идёт по
 * `status.in_(["planned", "rescheduled"])`). Перенесённая тренировка
 * состоится, её нагрузка считается и протокол ей нужен.
 */
export function isCalledOffStatus(status: string | null | undefined): boolean {
  return status === "cancelled";
}

export const STATUS_I18N_KEY: Record<string, string> = {
  planned:     "common:workout_type.planned",
  completed:   "common:workout_type.completed",
  done:        "common:workout_type.completed",
  cancelled:   "common:workout_type.cancelled",
  rescheduled: "common:workout_type.rescheduled",
};

// ---------- RPE ----------

// RPE 1–10 → цвет плашки
export function rpeColor(rpe: number | null | undefined): string {
  if (rpe == null) return "bg-bg-raised text-fg-muted border border-bg-border";
  if (rpe <= 3) return "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30";
  if (rpe <= 6) return "bg-accent/15 text-accent border border-accent/30";
  if (rpe <= 8) return "bg-amber-500/15 text-amber-300 border border-amber-500/30";
  return "bg-rose-500/15 text-rose-300 border border-rose-500/30";
}


// ---------- Sport visuals ----------

export type SportKey =
  | "run" | "trail"
  | "cycle" | "gravel"
  | "swim"
  | "triathlon" | "duathlon"
  | "hyrox"
  | "strength" | "other";

export type SportVisuals = {
  label: string;
  color: string;       // tailwind text class
  dot: string;         // tailwind bg class
};

// 2026-07-28: цвета обеих таблиц уведены на канон-токены дисциплин
// (--disc-*, определены в design-tokens/brand.css). До этого стояли сток-
// классы Tailwind и hex-литералы, и таблицы разошлись с каноном и между
// собой: вело было cyan (цвет ПЛАВАНИЯ) здесь и коралловым hex ниже,
// gravel — лаймом (цвет БЕГА), а trail/duathlon/hyrox красились снятыми
// сигнальными #1FD37C/#FFB23D/#FF3D6E — бывшими GOOD/WARN/BAD, которые
// канон заменил ещё 23.07. Токен вместо литерала заодно чинит темы:
// у --disc-bike есть light и три cb-варианта, hex не следовал ни одному.
const SPORT_VISUALS: Record<SportKey, SportVisuals> = {
  run:       { label: "Бег",      color: "text-disc-run",      dot: "bg-disc-run" },
  trail:     { label: "Трейл",    color: "text-disc-trail",    dot: "bg-disc-trail" },
  cycle:     { label: "Вело",     color: "text-disc-bike",     dot: "bg-disc-bike" },
  gravel:    { label: "Gravel",   color: "text-disc-gravel",   dot: "bg-disc-gravel" },
  swim:      { label: "Плавание", color: "text-disc-swim",     dot: "bg-disc-swim" },
  triathlon: { label: "Триатлон", color: "text-disc-tri",      dot: "bg-disc-tri" },
  duathlon:  { label: "Дуатлон",  color: "text-disc-duathlon", dot: "bg-disc-duathlon" },
  hyrox:     { label: "Hyrox",    color: "text-disc-hyrox",    dot: "bg-disc-hyrox" },
  strength:  { label: "Сила",     color: "text-disc-strength", dot: "bg-disc-strength" },
  // «Другое» — не дисциплина, канон-цвета у неё нет: третьестепенный текст.
  other:     { label: "Другое",   color: "text-fg-muted",      dot: "bg-fg-muted" },
};

const FALLBACK_SPORT: SportVisuals = SPORT_VISUALS.other;

/** Акцент дисциплины для borderTop/color на карточках (канон раздел 9 SPORT_COLOR).
 *  var(), а не hex: ни один use-site не конкатенирует альфу — проверено
 *  2026-07-28, все восемь ставят значение в `style` целиком (`color`,
 *  `background`, `borderTop`), прозрачность делается через `opacity`.
 *  MetricTile уже принимал `accentColor="var(--danger-500)"`, так что
 *  форма значения для потребителей не меняется. */
export const SPORT_COLOR: Record<SportKey, string> = {
  swim:      "var(--disc-swim)",
  cycle:     "var(--disc-bike)",
  gravel:    "var(--disc-gravel)",
  run:       "var(--disc-run)",
  trail:     "var(--disc-trail)",
  triathlon: "var(--disc-triathlon)",
  duathlon:  "var(--disc-duathlon)",
  hyrox:     "var(--disc-hyrox)",
  strength:  "var(--disc-strength)",
  other:     "var(--fg-muted)",
};

export function getSportColor(discipline: string | null | undefined): string {
  return SPORT_COLOR[getSportKey(discipline)] ?? SPORT_COLOR.other;
}

export function getSportKey(discipline: string | null | undefined): SportKey {
  if (!discipline) return "other";
  const d = discipline.toLowerCase();
  if (d === "trail" || d.startsWith("trail")) return "trail";
  if (d.startsWith("run")) return "run";
  if (d === "gravel") return "gravel";
  if (d === "ride" || d.startsWith("cycl") || d === "bike") return "cycle";
  if (d === "triathlon") return "triathlon";
  if (d === "duathlon") return "duathlon";
  if (d === "hyrox") return "hyrox";
  if (d.startsWith("swim")) return "swim";
  if (d.startsWith("strength") || d === "gym" || d.includes("weight")) return "strength";
  return "other";
}

export function getSportVisuals(discipline: string | null | undefined): SportVisuals {
  return SPORT_VISUALS[getSportKey(discipline)] || FALLBACK_SPORT;
}

export const SPORT_I18N_KEY: Record<SportKey, string> = {
  run:       "common:discipline.run",
  trail:     "common:discipline.trail",
  cycle:     "common:discipline.bike",
  gravel:    "common:discipline.gravel",
  swim:      "common:discipline.swim",
  triathlon: "common:discipline.tri",
  duathlon:  "common:discipline.duathlon",
  hyrox:     "common:discipline.hyrox",
  strength:  "common:discipline.strength",
  other:     "common:sport.other",
};

// ---------- Difficulty estimation ----------
//
// ═══ ЗЕРКАЛО. ИСТОЧНИК — shared/services/workout_difficulty.py ═══
//
// Одна шкала на все роли: тренер видит её через разбор (build_difficulty_view),
// атлет — здесь. Числа обязаны совпадать побайтово, и это держит тест
// tests/unit/test_difficulty_scale_parity.py — он читает ОБА исходника.
//
// ПОЧЕМУ ВЕТКА ДЛИТЕЛЬНОСТИ БОЛЬШЕ НЕ ЧИТАЕТ НАЗВАНИЕ — 2026-08-03.
// Здесь стояла своя последняя ступень: длительность × коэффициент, который
// выпаривался из названия набором регексов. Регексы были ru/en, поэтому у
// немецкого и украинского атлета не срабатывал ни один и коэффициент всегда
// оставался 0.8 — то есть ветка вырождалась в длительность с порогами
// 37.5/75/125, почти в точности бэкендовые 40/80/130. Эвристика вносила
// расхождение РОВНО ТАМ, где угадала язык, и нигде больше. Вырезана.

/**
 * Границы уровней 1..4. Порядок и значения — зеркало Python-констант
 * CTL_RATIO_BOUNDS / IF_BOUNDS / ABSOLUTE_LOAD_BOUNDS / DURATION_BOUNDS.
 * Сравнение строгое «<»: значение на границе идёт в верхний уровень.
 */
const CTL_RATIO_BOUNDS = [0.55, 0.95, 1.4] as const;
const IF_BOUNDS = [0.65, 0.85, 1.0] as const;
const ABSOLUTE_LOAD_BOUNDS = [50, 100, 150] as const;
const DURATION_BOUNDS = [40, 80, 130] as const;

export type DifficultyLevel = 1 | 2 | 3 | 4;

/** Уровень по трём границам — одна лестница на все четыре признака. */
function levelFrom(value: number, bounds: readonly [number, number, number]): DifficultyLevel {
  if (value < bounds[0]) return 1;
  if (value < bounds[1]) return 2;
  if (value < bounds[2]) return 3;
  return 4;
}

export type DifficultyVisuals = {
  level: DifficultyLevel;
  label: string;
  color: string; // text class
  bg: string;    // bg+border class
  dot: string;   // заливка точки — тот же тон, что и color
};

/* `dot` выписан литералом намеренно. Раньше он склеивался в рантайме
   (`color.replace("text-", "bg-")`), и Tailwind такую строку не видит: он
   сканирует исходники, а не значения. Из четырёх получавшихся классов в CSS
   существовал один — `bg-accent`; `bg-emerald-300` / `bg-amber-300` /
   `bg-rose-300` не эмитились никогда, потому что буквально в коде их не было.
   Литерал возвращает класс под надзор сборки и линтера мёртвых утилит. */
const DIFF_VISUALS: Record<DifficultyLevel, DifficultyVisuals> = {
  1: { level: 1, label: "Легко",   color: "text-emerald-300", bg: "bg-emerald-500/10 border border-emerald-500/30", dot: "bg-emerald-300" },
  2: { level: 2, label: "Средне",  color: "text-accent",       bg: "bg-accent/10 border border-accent/30",           dot: "bg-accent"      },
  3: { level: 3, label: "Тяжело",  color: "text-amber-300",    bg: "bg-amber-500/10 border border-amber-500/30",     dot: "bg-amber-300"   },
  4: { level: 4, label: "Жёстко",  color: "text-rose-300",     bg: "bg-rose-500/10 border border-rose-500/30",       dot: "bg-rose-300"    },
};

export type WorkoutLike = {
  planned_load?: number | null;
  planned_duration_minutes?: number | null;
  intensity_factor?: number | null;
  description?: string | null;
  title?: string | null;
};

export type DifficultyContext = {
  ctl_current?: number | null;
};

export function getDifficulty(
  w: WorkoutLike,
  ctx?: DifficultyContext,
): (DifficultyVisuals & { pct_of_ctl?: number }) | null {
  const load = w.planned_load;
  const ctl = ctx?.ctl_current;

  // 1. Лучший признак: нагрузка + хроническая → доля, персонально
  if (load != null && load > 0 && ctl != null && ctl > 0) {
    const ratio = load / ctl;
    return {
      ...DIFF_VISUALS[levelFrom(ratio, CTL_RATIO_BOUNDS)],
      pct_of_ctl: Math.round(ratio * 100),
    };
  }

  // 2. Интенсивность, когда нагрузки нет
  const if_ = w.intensity_factor;
  if (if_ != null && if_ > 0) return DIFF_VISUALS[levelFrom(if_, IF_BOUNDS)];

  // 3. Абсолютная нагрузка — доперсональный масштаб, только как запасной
  if (load != null && load > 0) return DIFF_VISUALS[levelFrom(load, ABSOLUTE_LOAD_BOUNDS)];

  // 4. Одни минуты
  const dur = w.planned_duration_minutes;
  if (dur == null || dur <= 0) return null;
  return DIFF_VISUALS[levelFrom(dur, DURATION_BOUNDS)];
}

// Маленький компонент-метка "уровня" — 4 точки
export type DiffDots = { filled: number; total: 4; cls: string };
export function difficultyDots(d: DifficultyVisuals | null): DiffDots {
  if (!d) return { filled: 0, total: 4, cls: "bg-fg-dim" };
  return { filled: d.level, total: 4, cls: d.dot };
}

export const DIFFICULTY_I18N_KEY: Record<DifficultyLevel, string> = {
  1: "common:difficulty.easy",
  2: "common:difficulty.medium",
  3: "common:difficulty.hard",
  4: "common:difficulty.brutal",
};
